import * as React from "react";
import { findAll } from "highlight-words-core";
import Linkify from "linkify-react";
import "linkify-plugin-mention";
import { FilterContext } from "../FeedFilter/State/FilterContext";
import { IUserDTO, useGroupContext } from "groopit-js";
import { useContext, useMemo } from "react";
import { getFilterKeywords } from "../FeedFilter/GroopitFilterDTO";

export const ExpandTags = (notes: string | undefined, users: IUserDTO[]) => {
  // note this regex has a capturing group around the guid. This is handy
  // because javascript split() will insert the capturing group into the array
  // eg "tagging @4261a1d9-b357-416e-7fd3-7a2bb2bd2f8b in this post".split(tag_re)
  // will return ["tagging ", "4261a1d9-b357-416e-7fd3-7a2bb2bd2f8b", " in this post"]
  // So even indices in the resulting array are text, and odd indices are the tag.
  const tag_re = /@([0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12})/g;

  return notes
    ?.split(tag_re)
    .map((n, i) =>
      i % 2 === 0 ? n : users.find((u) => u.id == n)?.friendlyName ?? "@" + n,
    )
    .join("");
};

const TaggedUser = (id: string) => {
  const { users } = useGroupContext();
  const user = users.find((u) => u.id === id);
  const uuid_re = useMemo(
    () => /[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}/g,
    [],
  );
  const isUUID = useMemo(() => uuid_re.test(id), [id, uuid_re]);

  if (isUUID) {
    return (
      <button className="tag ping" type="button">
        {user?.friendlyName ?? "---"}
      </button>
    );
  } else {
    return <span>@{id}</span>;
  }
};

//
// It would be great to use Highlighter from highlight-words-core, but it doesn't
// play well with Linkify, so here is a quick and dirty reimplementation.
//
// If they ever ship a release newer than 0.18.0 we could remove this in favor
// of the new unhighlightTag prop https://github.com/bvaughn/react-highlight-words/pull/99
//
interface HighlighterProps {
  searchWords: string[];
  textToHighlight: string;
  highlightClassName?: string | undefined;
}
const Highlighter = (props: HighlighterProps) => {
  const { searchWords, textToHighlight, highlightClassName } = props;
  const chunks = findAll({
    autoEscape: true,
    caseSensitive: false,
    searchWords,
    textToHighlight,
  });

  return (
    <>
      {chunks.map((chunk, i) => {
        const { end, highlight, start } = chunk;
        const text = textToHighlight.slice(start, end);
        return highlight ? (
          <mark key={i} className={highlightClassName}>
            {text}
          </mark>
        ) : (
          <Linkify key={i} options={LinkifyOptions}>
            {text}
          </Linkify>
        );
      })}
    </>
  );
};

const LinkifyOptions = {
  render: {
    mention: (props: { content: string }) => {
      const { content } = props;
      return TaggedUser(content.slice(1)); // strip off the initial '@'
    },
  },
};

interface NoteProperties {
  notes?: string;
}

export const NoteComponent = (props: NoteProperties) => {
  const filterContext = useContext(FilterContext);
  const searchWords = useMemo(
    () => getFilterKeywords(filterContext?.filter),
    [filterContext?.filter],
  );
  const { notes } = props;

  return (
    <div className="text-display-normal text-color-secondary break-words note-autocomplete note-component whitespace-pre-wrap">
      {searchWords.length ? (
        <Highlighter
          searchWords={searchWords}
          textToHighlight={notes ?? ""}
          highlightClassName="search-highlight"
        ></Highlighter>
      ) : (
        <Linkify options={LinkifyOptions}>{notes}</Linkify>
      )}
    </div>
  );
};
